import React from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import {
  ContractAdjustmentView,
  ContractCeilingView,
  validateWizardStep1,
  hasQualifyingEvidence,
  getDefaultDirection,
  isDirectionAllowed,
} from "./contractAdjustmentsUtils";
import {
  UseContractAdjustmentEvidenceReturn,
  useContractAdjustmentEvidence,
} from "./useContractAdjustmentEvidence";

export type WizardStep = 1 | 2 | 3;
export type AdjustmentType = "contract_extension" | "additional_work" | "price_revision" | "contract_reduction" | "other";
export type Direction = "increase" | "decrease";

export type FormData = {
  title: string;
  reason: string;
  adjustmentType: AdjustmentType;
  direction: Direction;
  amount: string;
  effectiveDate: string;
  reference: string;
};

export const INITIAL_FORM_DATA: FormData = {
  title: "", reason: "", adjustmentType: "contract_extension",
  direction: "increase", amount: "", effectiveDate: "", reference: "",
};

export const ADJUSTMENT_TYPES: { value: AdjustmentType; label: string }[] = [
  { value: "contract_extension", label: "Ampliación de contrato" },
  { value: "additional_work", label: "Trabajo adicional" },
  { value: "price_revision", label: "Revisión de precio" },
  { value: "contract_reduction", label: "Reducción de alcance" },
  { value: "other", label: "Otro" },
];

export interface UseContractAdjustmentWizardOptions {
  installationId: number;
  existingAdjustment?: ContractAdjustmentView | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  adjustmentsRefetch: () => void;
  ceilingRefetch: () => void;
}

export interface UseContractAdjustmentWizardReturn {
  step: WizardStep;
  goNext: () => Promise<void>;
  goBack: () => void;
  activeAdjustmentId: number | undefined;
  formData: FormData;
  errors: Record<string, string>;
  handleInputChange: (field: keyof FormData, value: string) => void;
  handleAdjustmentTypeChange: (value: string) => void;
  handleSubmit: () => Promise<void>;
  handleSaveDraft: () => Promise<void>;
  isSubmitting: boolean;
  isCreatingDraft: boolean;
  isAnyPending: boolean;
  canSubmit: boolean;
  showCloseConfirm: boolean;
  closeConfirmMessage: string;
  closeConfirmTitle: string;
  closeConfirmPrimaryLabel: string;
  closeConfirmPrimaryVariant: "default" | "destructive";
  handleClose: () => void;
  confirmClose: () => void;
  cancelClose: () => void;
  ceiling: ContractCeilingView | null;
  isEdit: boolean;
  isPendingEvidenceMode: boolean;
  evidence: UseContractAdjustmentEvidenceReturn;
}

export function useContractAdjustmentWizard(
  options: UseContractAdjustmentWizardOptions,
): UseContractAdjustmentWizardReturn {
  const { installationId, existingAdjustment, open, onOpenChange, onSuccess, adjustmentsRefetch, ceilingRefetch } = options;
  const isEdit = Boolean(existingAdjustment);
  const isPendingEvidenceMode = isEdit && existingAdjustment?.status === "pending_approval";

  const [activeAdjustmentId, setActiveAdjustmentId] = React.useState<number | undefined>(existingAdjustment?.id);
  const submittingRef = React.useRef(false);

  // Evidence hook called inside — receives reactive activeAdjustmentId
  const evidence = useContractAdjustmentEvidence({
    installationId, activeAdjustmentId, open, isEdit,
  });

  const [step, setStep] = React.useState<WizardStep>(1);
  const initialFormDataRef = React.useRef<FormData | null>(null);
  const [formData, setFormData] = React.useState<FormData>(() => {
    const fd = existingAdjustment ? existingToFormData(existingAdjustment) : { ...INITIAL_FORM_DATA };
    initialFormDataRef.current = fd;
    return fd;
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [unsavedChanges, setUnsavedChanges] = React.useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  const [closeConfirmMessage, setCloseConfirmMessage] = React.useState("");

  // ── Queries ─────────────────────────────────────────────────────────
  const ceilingQuery = trpc.contractAdjustments.getEffectiveContractCeiling.useQuery(
    { installationId }, { enabled: open },
  );
  const ceiling = (ceilingQuery.data as ContractCeilingView | null) ?? null;

  // ── Mutations ───────────────────────────────────────────────────────
  const createDraftMutation = trpc.contractAdjustments.createDraft.useMutation();
  const updateDraftMutation = trpc.contractAdjustments.updateDraft.useMutation();
  const submitMutation = trpc.contractAdjustments.submitForApproval.useMutation();

  // ── Reset on open ───────────────────────────────────────────────────
  React.useEffect(() => {
    if (open) {
      setStep(1);
      setErrors({});
      setUnsavedChanges(false);
      setShowCloseConfirm(false);
      submittingRef.current = false;
      if (!existingAdjustment) {
        const fd = { ...INITIAL_FORM_DATA };
        setFormData(fd);
        initialFormDataRef.current = fd;
        setActiveAdjustmentId(undefined);
      } else {
        const fd = existingToFormData(existingAdjustment);
        setFormData(fd);
        initialFormDataRef.current = fd;
        setActiveAdjustmentId(existingAdjustment.id);
      }
    }
  }, [open, existingAdjustment]);

  // ── Form change detection ───────────────────────────────────────────
  const formDataChanged = React.useMemo(() => {
    if (!initialFormDataRef.current) return true;
    for (const key of Object.keys(formData) as (keyof FormData)[]) {
      if (formData[key] !== initialFormDataRef.current[key]) return true;
    }
    return false;
  }, [formData]);

  // ── Handlers ────────────────────────────────────────────────────────
  const markChanged = React.useCallback(() => setUnsavedChanges(true), []);

  const handleInputChange = React.useCallback(
    (field: keyof FormData, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
      markChanged();
      if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    },
    [errors, markChanged],
  );

  // P1.3: auto-correct direction when type changes
  const handleAdjustmentTypeChange = React.useCallback(
    (value: string) => {
      setFormData(prev => {
        const currentDirection = prev.direction;
        if (!isDirectionAllowed(value, currentDirection)) {
          const defaultDir = getDefaultDirection(value);
          return { ...prev, adjustmentType: value as AdjustmentType, direction: defaultDir };
        }
        return { ...prev, adjustmentType: value as AdjustmentType };
      });
      markChanged();
      if (errors.adjustmentType) setErrors(prev => { const n = { ...prev }; delete n.adjustmentType; return n; });
    },
    [errors, markChanged],
  );

  // ── Navigation ──────────────────────────────────────────────────────
  const goNext = React.useCallback(async () => {
    if (isPendingEvidenceMode) return;
    if (submittingRef.current) return;

    if (step !== 1) {
      setStep(prev => (prev + 1) as WizardStep);
      return;
    }

    // Step 1 → validate then create/update draft
    const validation = validateWizardStep1(formData);
    if (!validation.valid) { setErrors(validation.errors); return; }
    setErrors({});
    submittingRef.current = true;

    try {
      if (activeAdjustmentId) {
        if (formDataChanged) {
          await updateDraftMutation.mutateAsync({
            id: activeAdjustmentId,
            data: {
              title: formData.title.trim(), reason: formData.reason.trim(),
              adjustmentType: formData.adjustmentType, direction: formData.direction,
              amount: formData.amount.trim(), effectiveDate: formData.effectiveDate || null,
              reference: formData.reference.trim() || null,
            },
          });
        }
      } else {
        const draft = await createDraftMutation.mutateAsync({
          installationId, title: formData.title.trim(), reason: formData.reason.trim(),
          adjustmentType: formData.adjustmentType, direction: formData.direction,
          amount: formData.amount.trim(), effectiveDate: formData.effectiveDate || null,
          reference: formData.reference.trim() || null,
        });
        const newId = (draft as any).id;
        if (!newId) throw new Error("No se pudo crear el borrador.");
        setActiveAdjustmentId(newId);
      }
      // P0.2: Update snapshot — subsequent goBack → goNext won't re-update
      initialFormDataRef.current = { ...formData };
      setUnsavedChanges(false);
      adjustmentsRefetch();
      setStep(2);
    } catch (error: any) {
      toast.error(error?.message || "Error al guardar el borrador.");
    } finally {
      submittingRef.current = false;
    }
  }, [step, formData, formDataChanged, activeAdjustmentId, isPendingEvidenceMode, installationId, adjustmentsRefetch, createDraftMutation, updateDraftMutation]);

  const goBack = React.useCallback(() => {
    if (isPendingEvidenceMode) return;
    setStep(prev => (prev - 1) as WizardStep);
  }, [isPendingEvidenceMode]);

  // ── Close ───────────────────────────────────────────────────────────
  const isAnyPendingLocal = React.useMemo(() => {
    return submittingRef.current || submitMutation.isPending || evidence.isUploading || evidence.isAttaching || evidence.isDetaching || evidence.isUpdatingType;
  }, [submitMutation.isPending, evidence.isUploading, evidence.isAttaching, evidence.isDetaching, evidence.isUpdatingType]);

  const handleClose = React.useCallback(() => {
    if (isPendingEvidenceMode) {
      if (isAnyPendingLocal) {
        setCloseConfirmMessage("Las evidencias guardadas se conservarán.");
        setShowCloseConfirm(true);
      } else {
        onOpenChange(false);
      }
      return;
    }
    if (activeAdjustmentId) {
      setCloseConfirmMessage("Se ha guardado un borrador de esta modificación. Podrás continuarla desde el listado.");
      setShowCloseConfirm(true);
    } else if (unsavedChanges) {
      setCloseConfirmMessage("Hay cambios sin guardar. Si cierras ahora, se perderán.");
      setShowCloseConfirm(true);
    } else {
      onOpenChange(false);
    }
  }, [isPendingEvidenceMode, activeAdjustmentId, unsavedChanges, isAnyPendingLocal, onOpenChange]);

  const confirmClose = React.useCallback(() => {
    setShowCloseConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const cancelClose = React.useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  // Close confirm dialog title/button labels
  const closeConfirmTitle = activeAdjustmentId && !isPendingEvidenceMode
    ? "Borrador guardado"
    : isPendingEvidenceMode
      ? "Evidencias guardadas"
      : "¿Descartar cambios?";
  const closeConfirmPrimaryLabel = activeAdjustmentId && !isPendingEvidenceMode
    ? "Salir y conservar borrador"
    : "Cerrar";
  const closeConfirmPrimaryVariant = closeConfirmPrimaryLabel === "Cerrar" ? "default" as const : "default" as const;
  // For "¿Descartar cambios?" the primary is "Descartar" with destructive variant
  const finalPrimaryLabel = !activeAdjustmentId ? "Descartar" : closeConfirmPrimaryLabel;
  const finalPrimaryVariant = !activeAdjustmentId ? "destructive" as const : closeConfirmPrimaryVariant;

  // ── Save draft ──────────────────────────────────────────────────────
  const handleSaveDraft = React.useCallback(async () => {
    // Data is already persisted (step 1 created/updated it; evidence ops are real-time)
    adjustmentsRefetch();
    toast.success("Borrador guardado.");
    onOpenChange(false);
  }, [adjustmentsRefetch, onOpenChange]);

  // ── Submit ──────────────────────────────────────────────────────────
  const handleSubmit = React.useCallback(async () => {
    const id = activeAdjustmentId;
    if (!id) { toast.error("No hay borrador activo."); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      // Re-read evidence from backend (authoritative)
      const backendEvidence = await evidence.readEvidenceFromBackend();
      const types = backendEvidence.map(e => e.evidenceType);
      if (!hasQualifyingEvidence(types)) {
        throw new Error(
          "No hay evidencias cualificantes para aprobación. " +
          "La modificación necesita al menos una evidencia firmada (anexo, contrato, pedido o aceptación por correo).",
        );
      }
      // Submit for approval (single API call)
      await submitMutation.mutateAsync({ id });
      toast.success(isEdit ? "Modificación actualizada y enviada a aprobación" : "Modificación creada y enviada a aprobación");
      adjustmentsRefetch();
      ceilingRefetch();
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.message || "Error al enviar la modificación");
    } finally {
      submittingRef.current = false;
    }
  }, [activeAdjustmentId, evidence, isEdit, submitMutation, adjustmentsRefetch, ceilingRefetch, onSuccess, onOpenChange]);

  // ── Derived ─────────────────────────────────────────────────────────
  const isCreatingDraft = createDraftMutation.isPending;
  const isSubmitting = submittingRef.current || submitMutation.isPending;
  const isAnyPending = isAnyPendingLocal || isCreatingDraft || createDraftMutation.isPending || updateDraftMutation.isPending;
  const canSubmit = evidence.canSubmitEvidence;

  return {
    step, goNext, goBack, activeAdjustmentId,
    formData, errors, handleInputChange, handleAdjustmentTypeChange,
    handleSubmit, handleSaveDraft,
    isSubmitting, isCreatingDraft, isAnyPending, canSubmit,
    showCloseConfirm, closeConfirmMessage,
    closeConfirmTitle: finalPrimaryLabel, // simple title
    closeConfirmPrimaryLabel: finalPrimaryLabel,
    closeConfirmPrimaryVariant: finalPrimaryVariant,
    handleClose, confirmClose, cancelClose,
    ceiling, isEdit, isPendingEvidenceMode, evidence,
  };
}

// ── Pure helper ──────────────────────────────────────────────────────
function existingToFormData(adj: ContractAdjustmentView): FormData {
  return {
    title: adj.title || "",
    reason: adj.reason || "",
    adjustmentType: (adj.adjustmentType as AdjustmentType) || "contract_extension",
    direction: (adj.direction as Direction) || "increase",
    amount: typeof adj.amount === "string" ? adj.amount : String(adj.amount || ""),
    effectiveDate: adj.effectiveDate ? new Date(adj.effectiveDate).toISOString().split("T")[0] : "",
    reference: adj.reference || "",
  };
}